import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Coins, RefreshCw, ShieldCheck, XCircle } from "lucide-react";

import api from "../api";

function formatGrams(value) {
  return `${Number(value || 0).toFixed(3)} g`;
}

function formatRs(value) {
  return `Rs. ${Number(value || 0).toFixed(2)}`;
}

function MetalExchangeAdminControl() {
  const [clientRows, setClientRows] = useState([]);
  const [approvalRows, setApprovalRows] = useState([]);
  const [draftLimits, setDraftLimits] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, approvalsRes] = await Promise.all([
        api.get("/metal-exchange/admin/client-limits"),
        api.get("/metal-exchange/admin/approval-requests?status=PENDING"),
      ]);
      const clients = clientsRes.data?.data || [];
      const approvals = approvalsRes.data?.data || [];
      setClientRows(clients);
      setApprovalRows(approvals);
      setDraftLimits(
        clients.reduce((acc, row) => {
          acc[row.customer_id] = {
            issue_limit_g: String(row.issue_limit_g ?? 500),
            overdraft_limit_g: String(row.overdraft_limit_g ?? 0),
          };
          return acc;
        }, {})
      );
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load metal exchange admin controls.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(
    () => ({
      pending: approvalRows.length,
      totalExposure: clientRows.reduce((sum, row) => sum + Number(row.open_issue_exposure_g || 0), 0),
      blocked: clientRows.filter((row) => row.metal_exchange_blocked).length,
    }),
    [approvalRows, clientRows]
  );

  const saveClientLimit = async (customerId) => {
    const draft = draftLimits[customerId];
    if (!draft) return;
    setSavingKey(`limit-${customerId}`);
    try {
      await api.patch(`/metal-exchange/clients/${customerId}/account?metal_type=SILVER`, {
        issue_limit_g: Number(draft.issue_limit_g || 500),
        overdraft_limit_g: Number(draft.overdraft_limit_g || 0),
      });
      setMessage("Client metal limits updated.");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update client limit.");
    } finally {
      setSavingKey("");
    }
  };

  const approveRequest = async (requestRow) => {
    setSavingKey(`approve-${requestRow.id}`);
    try {
      await api.post(`/metal-exchange/admin/approval-requests/${requestRow.id}/approve`, {
        approved_issue_limit_g: Number(
          draftLimits[requestRow.customer_id]?.issue_limit_g || requestRow.requested_issue_limit_g || requestRow.projected_issue_exposure_g
        ),
      });
      setMessage(`Approval request #${requestRow.id} approved and transaction created.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to approve metal issue request.");
    } finally {
      setSavingKey("");
    }
  };

  const rejectRequest = async (requestId) => {
    setSavingKey(`reject-${requestId}`);
    try {
      await api.post(`/metal-exchange/admin/approval-requests/${requestId}/reject`, {
        rejection_reason: "Rejected by admin after limit review.",
      });
      setMessage(`Approval request #${requestId} rejected.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reject metal issue request.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <div className="space-y-6">
      {(message || error) && (
        <div className={`rounded-[1.75rem] border px-5 py-4 text-sm shadow-sm ${error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {error || message}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        <AdminKpi icon={ShieldCheck} label="Pending Requests" value={totals.pending} tone="amber" />
        <AdminKpi icon={Coins} label="Open Exposure" value={formatGrams(totals.totalExposure)} tone="indigo" />
        <AdminKpi icon={XCircle} label="Blocked Clients" value={totals.blocked} tone="rose" />
      </section>

      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.16),_transparent_45%),linear-gradient(135deg,#0f172a_0%,#1e293b_60%,#334155_100%)] px-6 py-5 text-white">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-indigo-200">Metal Exchange Control</div>
            <h3 className="mt-2 text-2xl font-bold">Client Limits</h3>
            <p className="mt-1 text-sm text-slate-200">Admin sets client issue and overdraft limits here. Accountant can only raise requests.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.22em] text-slate-500">
              <tr>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Issue Limit</th>
                <th className="px-4 py-3">Overdraft</th>
                <th className="px-4 py-3">Open Exposure</th>
                <th className="px-4 py-3">Current Balance</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && clientRows.map((row) => (
                <tr key={row.customer_id} className="border-t border-slate-100">
                  <td className="px-4 py-4">
                    <div className="font-semibold text-slate-900">{row.customer_name}</div>
                    <div className="text-xs text-slate-500">{row.customer_code}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${row.metal_exchange_rating <= 2 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {row.metal_exchange_rating}/5
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <input
                      type="number"
                      value={draftLimits[row.customer_id]?.issue_limit_g || ""}
                      onChange={(event) =>
                        setDraftLimits((prev) => ({
                          ...prev,
                          [row.customer_id]: { ...prev[row.customer_id], issue_limit_g: event.target.value },
                        }))
                      }
                      className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <input
                      type="number"
                      value={draftLimits[row.customer_id]?.overdraft_limit_g || ""}
                      onChange={(event) =>
                        setDraftLimits((prev) => ({
                          ...prev,
                          [row.customer_id]: { ...prev[row.customer_id], overdraft_limit_g: event.target.value },
                        }))
                      }
                      className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </td>
                  <td className="px-4 py-4 font-semibold text-slate-700">{formatGrams(row.open_issue_exposure_g)}</td>
                  <td className="px-4 py-4 text-slate-600">{formatGrams(row.current_balance_g)}</td>
                  <td className="px-4 py-4">
                    <div className="flex justify-end">
                      <button
                        onClick={() => saveClientLimit(row.customer_id)}
                        disabled={savingKey === `limit-${row.customer_id}`}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:opacity-60"
                      >
                        <ShieldCheck size={15} /> Save Limits
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !clientRows.length && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-slate-500">No client accounts found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500">Approval Queue</div>
          <h3 className="mt-2 text-2xl font-bold text-slate-900">Pending Metal Issue Requests</h3>
          <p className="mt-1 text-sm text-slate-500">Approving a request updates the client limit if needed and creates the metal issue transaction automatically.</p>
        </div>

        <div className="grid gap-4 p-6">
          {!loading && approvalRows.map((row) => (
            <div key={row.id} className="rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Request #{row.id}</div>
                  <h4 className="mt-2 text-xl font-bold text-slate-900">{row.customer_name}</h4>
                  <div className="mt-1 text-sm text-slate-500">
                    Raised by {row.requested_by_name || "-"} | Reason: {row.approval_reason}
                  </div>
                </div>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">{row.status}</span>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <RequestStat label="Requested Qty" value={formatGrams(row.quantity_g)} />
                <RequestStat label="Rate" value={formatRs(row.rate_per_g)} />
                <RequestStat label="Amount" value={formatRs(row.amount_rs)} />
                <RequestStat label="Current Limit" value={formatGrams(row.current_issue_limit_g)} />
                <RequestStat label="Proposed New Limit" value={formatGrams(row.requested_issue_limit_g)} />
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-[1fr_auto_auto]">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Due {row.due_date} | Deadline {row.deadline_date} | Quantity requested by accountant: {formatGrams(row.quantity_g)}
                  {row.notes ? ` | Notes: ${row.notes}` : ""}
                </div>
                <button
                  onClick={() => rejectRequest(row.id)}
                  disabled={savingKey === `reject-${row.id}` || savingKey === `approve-${row.id}`}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                >
                  <XCircle size={15} /> Reject
                </button>
                <button
                  onClick={() => approveRequest(row)}
                  disabled={savingKey === `approve-${row.id}` || savingKey === `reject-${row.id}`}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-indigo-500 disabled:opacity-60"
                >
                  <CheckCircle2 size={15} /> Approve and Create TX
                </button>
              </div>
            </div>
          ))}

          {!loading && !approvalRows.length && (
            <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
              No pending metal issue approval requests.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AdminKpi({ icon: Icon, label, value, tone }) {
  const palette = {
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };

  return (
    <div className={`rounded-[1.75rem] border p-5 shadow-sm ${palette[tone] || palette.indigo}`}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon size={16} />
        <span>{label}</span>
      </div>
      <div className="mt-3 text-2xl font-bold">{value}</div>
    </div>
  );
}

function RequestStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="mt-2 text-base font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default MetalExchangeAdminControl;
